import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    getDirectoryAccess,
    saveDirectoryAccess,
    getDirectoryAccessSubjects,
} from '../../../services/api';
import {
    AlertTriangle,
    Check,
    ChevronDown,
    ChevronRight,
    Folder,
    Loader2,
    Lock,
    Plus,
    Search,
    Shield,
    ShieldAlert,
    Trash2,
    Unlock,
    Users,
    X,
} from 'lucide-react';

const PERM_KEYS = ['canView', 'canUpdate', 'canDelete', 'canCreateChildren'];
const PERM_LABELS = { canView: 'Voir', canUpdate: 'Modifier', canDelete: 'Supprimer', canCreateChildren: 'Créer enfant' };

function PermBadge({ granted }) {
    return granted ? (
        <Check size={14} className="text-emerald-600" />
    ) : (
        <X size={14} className="text-stone-300" />
    );
}

function GrantSubjectLabel({ grant }) {
    if (grant.grantType === 'user') {
        return <span className="font-medium text-stone-800">{grant.targetName}</span>;
    }
    if (grant.grantType === 'wiki_role') {
        return (
            <span className="font-medium text-stone-800">
                <span className="text-teal-600 text-xs mr-1">Wiki</span>
                {grant.roleName || grant.roleId}
                {grant.wikiGroupName && (
                    <span className="text-stone-400 text-xs ml-1">({grant.wikiGroupName})</span>
                )}
            </span>
        );
    }
    return (
        <span className="font-medium text-stone-800">
            <span className="text-iris-600 text-xs mr-1">Rôle</span>
            {grant.roleName || grant.roleId}
            {grant.projectName && (
                <span className="text-stone-400 text-xs ml-1">({grant.projectName})</span>
            )}
        </span>
    );
}

function InheritanceBadge({ inherited }) {
    return inherited ? (
        <span className="text-3xs px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 font-medium">Hérité</span>
    ) : (
        <span className="text-3xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">Local</span>
    );
}

function ChildStatusBadge({ child }) {
    if (child.overrideAccess) {
        return (
            <span className="inline-flex items-center gap-1 text-2xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium">
                <Lock size={10} /> Héritage coupé
            </span>
        );
    }
    if (child.hasLocalGrants) {
        return (
            <span className="inline-flex items-center gap-1 text-2xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">
                <Shield size={10} /> {child.localGrantCount} grant{child.localGrantCount > 1 ? 's' : ''} local{child.localGrantCount > 1 ? 'aux' : ''}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 text-2xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">
            <Unlock size={10} /> Hérite du parent
        </span>
    );
}

const WIKI_GROUPS_URL = '/decoupled-api/group/wiki/collection?display=decoupled';

export default function DocAccessPanel({ directoryId, isActive, onSaved, readOnly: readOnlyProp = false }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [accessDenied, setAccessDenied] = useState(false);
    const [backendReadOnly, setBackendReadOnly] = useState(false);
    const readOnly = readOnlyProp || backendReadOnly;

    const [info, setInfo] = useState(null);
    const [localGrants, setLocalGrants] = useState([]);
    const [overrideAccess, setOverrideAccess] = useState(false);

    const [subjects, setSubjects] = useState(null);
    const [subjectsLoading, setSubjectsLoading] = useState(false);

    const [allWikiGroups, setAllWikiGroups] = useState([]);
    const [wikiGroupsLoading, setWikiGroupsLoading] = useState(false);

    const [showAddForm, setShowAddForm] = useState(false);
    const [addType, setAddType] = useState('user');
    const [addTargetId, setAddTargetId] = useState('');
    const [addRoleId, setAddRoleId] = useState('');
    const [addWikiGroupId, setAddWikiGroupId] = useState('');
    const [wikiGroupQuery, setWikiGroupQuery] = useState('');
    const [showWikiGroupDropdown, setShowWikiGroupDropdown] = useState(false);
    const wikiGroupRef = useRef(null);
    const [addPerms, setAddPerms] = useState({ canView: true, canUpdate: false, canDelete: false, canCreateChildren: false });

    const [childrenOpen, setChildrenOpen] = useState(false);

    const loadData = useCallback(async () => {
        if (!directoryId) return;
        setLoading(true);
        setError('');
        setAccessDenied(false);
        try {
            const data = await getDirectoryAccess(directoryId);
            setInfo(data);
            setLocalGrants(data.localGrants || []);
            setOverrideAccess(data.overrideAccess || false);
            if (data.readOnly != null) setBackendReadOnly(!!data.readOnly);
        } catch (err) {
            if (err.status === 403) {
                setAccessDenied(true);
            } else {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    }, [directoryId]);

    useEffect(() => {
        if (isActive && directoryId) loadData();
    }, [isActive, directoryId, loadData]);

    const loadSubjects = useCallback(async () => {
        if (subjects || subjectsLoading || !directoryId) return;
        setSubjectsLoading(true);
        try {
            const data = await getDirectoryAccessSubjects(directoryId);
            setSubjects(data);
        } catch {
            setSubjects({ users: [], projectMembers: [], availableRoles: [], wikiGroups: [], availableWikiRoles: [] });
        } finally {
            setSubjectsLoading(false);
        }
    }, [directoryId, subjects, subjectsLoading]);

    const loadWikiGroups = useCallback(async () => {
        if (allWikiGroups.length > 0 || wikiGroupsLoading) return;
        setWikiGroupsLoading(true);
        try {
            const res = await fetch(WIKI_GROUPS_URL, { credentials: 'include' });
            if (!res.ok) throw new Error(`${res.status}`);
            const data = await res.json();
            const groups = (Array.isArray(data) ? data : [])
                .filter((item) => item?.data ?? item?.id != null)
                .map((item) => {
                    const d = item.data ?? item;
                    return { id: typeof d.id === 'string' ? parseInt(d.id, 10) : d.id, name: d.name || '' };
                });
            setAllWikiGroups(groups);
        } catch {
            setAllWikiGroups([]);
        } finally {
            setWikiGroupsLoading(false);
        }
    }, [allWikiGroups.length, wikiGroupsLoading]);

    const wikiGroupSource = useMemo(
        () => allWikiGroups.length > 0 ? allWikiGroups : (subjects?.wikiGroups || []).map((g) => ({ id: g.id, name: g.name })),
        [allWikiGroups, subjects],
    );

    const filteredWikiGroups = useMemo(() => {
        const q = wikiGroupQuery.trim().toLowerCase();
        if (!q) return wikiGroupSource;
        return wikiGroupSource.filter((g) => g.name.toLowerCase().includes(q));
    }, [wikiGroupSource, wikiGroupQuery]);

    useEffect(() => {
        if (!showWikiGroupDropdown) return;
        const handleClickOutside = (e) => {
            if (wikiGroupRef.current && !wikiGroupRef.current.contains(e.target)) {
                setShowWikiGroupDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showWikiGroupDropdown]);

    const handleToggleLocalPerm = useCallback((idx, perm) => {
        setLocalGrants((prev) => prev.map((g, i) => (i !== idx ? g : { ...g, [perm]: !g[perm] })));
    }, []);

    const handleRemoveLocal = useCallback((idx) => {
        setLocalGrants((prev) => prev.filter((_, i) => i !== idx));
    }, []);

    const handleAddGrant = useCallback(() => {
        if (addType === 'wiki_role' ? !addWikiGroupId : !addTargetId) return;
        const newGrant = {
            grantType: addType,
            targetId: addType === 'wiki_role' ? parseInt(addWikiGroupId, 10) : parseInt(addTargetId, 10),
            targetName: '',
            roleId: addType === 'project_role' ? addRoleId : '',
            roleName: '',
            projectName: '',
            wikiGroupName: '',
            ...addPerms,
            isInherited: false,
        };
        if (addType === 'user' && subjects?.users) {
            const u = subjects.users.find((s) => String(s.id) === String(newGrant.targetId));
            if (u) newGrant.targetName = u.name;
        }
        if (addType === 'project_role') {
            newGrant.targetName = info?.projectName || '';
            newGrant.projectName = info?.projectName || '';
            if (subjects?.availableRoles) {
                const r = subjects.availableRoles.find((s) => String(s.id) === String(addRoleId));
                if (r) newGrant.roleName = r.label;
            }
        }
        if (addType === 'wiki_role') {
            const wg = wikiGroupSource.find((g) => String(g.id) === String(addWikiGroupId));
            newGrant.wikiGroupName = wg?.name || '';
            newGrant.targetName = wg?.name || '';
        }
        setLocalGrants((prev) => [...prev, newGrant]);
        setAddTargetId('');
        setAddRoleId('');
        setAddWikiGroupId('');
        setWikiGroupQuery('');
        setAddPerms({ canView: true, canUpdate: false, canDelete: false, canCreateChildren: false });
        setShowAddForm(false);
    }, [addType, addTargetId, addRoleId, addWikiGroupId, addPerms, subjects, info, wikiGroupSource]);

    const handleSave = useCallback(async () => {
        setSaving(true);
        setError('');
        try {
            const payload = {
                overrideAccess,
                accessGrants: localGrants.map((g) => ({
                    grantType: g.grantType,
                    targetId: g.targetId,
                    roleId: g.roleId || '',
                    canView: g.canView,
                    canUpdate: g.canUpdate,
                    canDelete: g.canDelete,
                    canCreateChildren: g.canCreateChildren,
                })),
            };
            const updated = await saveDirectoryAccess(directoryId, payload);
            setInfo(updated);
            setLocalGrants(updated.localGrants || []);
            setOverrideAccess(updated.overrideAccess || false);
            onSaved?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }, [directoryId, localGrants, overrideAccess, onSaved]);

    const hasProject = useMemo(() => !!info?.projectId, [info]);
    const isRoot = useMemo(() => info?.isRoot ?? true, [info]);
    const effectiveGrants = useMemo(() => info?.effectiveGrants || [], [info]);
    const children = useMemo(() => info?.children || [], [info]);

    if (!isActive) return null;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-stone-400">
                <Loader2 size={20} className="animate-spin mr-2" /> Chargement…
            </div>
        );
    }

    if (accessDenied) {
        return (
            <div className="flex flex-col items-center justify-center py-14 text-center">
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
                    <ShieldAlert size={24} className="text-amber-500" />
                </div>
                <p className="text-sm font-medium text-stone-700 mb-1">Accès restreint</p>
                <p className="text-xs text-stone-400 max-w-xs">
                    Vous n'avez pas les droits nécessaires pour consulter les accès de ce répertoire.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {!isRoot && (
                <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200 bg-stone-50">
                    <button
                        type="button"
                        onClick={() => { if (!readOnly) setOverrideAccess((v) => !v); }}
                        disabled={readOnly}
                        className={`mt-0.5 relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${readOnly ? 'cursor-default opacity-60' : 'cursor-pointer'} ${overrideAccess ? 'bg-amber-500' : 'bg-stone-300'}`}
                    >
                        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${overrideAccess ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-stone-800">Couper l'héritage</p>
                        <p className="text-xs text-stone-500 mt-0.5">
                            Si activé, ce répertoire n'héritera plus des droits de son parent.
                        </p>
                        {overrideAccess && (
                            <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg">
                                <AlertTriangle size={12} />
                                Seuls les grants locaux ci-dessous s'appliqueront.
                            </div>
                        )}
                    </div>
                </div>
            )}

            <section>
                <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Users size={13} /> Accès effectifs
                    <span className="text-stone-300 font-normal normal-case tracking-normal">({effectiveGrants.length})</span>
                </h3>
                {effectiveGrants.length === 0 ? (
                    <p className="text-sm text-stone-400 italic">Aucun accès défini.</p>
                ) : (
                    <div className="border border-stone-200 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-stone-50 text-stone-500 text-xs">
                                    <th className="text-left px-3 py-2 font-medium">Sujet</th>
                                    <th className="text-left px-3 py-2 font-medium w-14">Source</th>
                                    {PERM_KEYS.map((k) => (
                                        <th key={k} className="text-center px-1 py-2 font-medium w-16">{PERM_LABELS[k]}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {effectiveGrants.map((g, i) => (
                                    <tr key={i} className={g.isInherited ? 'bg-stone-50/50 text-stone-500 italic' : ''}>
                                        <td className="px-3 py-2"><GrantSubjectLabel grant={g} /></td>
                                        <td className="px-3 py-2"><InheritanceBadge inherited={g.isInherited} /></td>
                                        {PERM_KEYS.map((k) => (
                                            <td key={k} className="text-center px-1 py-2"><PermBadge granted={g[k]} /></td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest flex items-center gap-2">
                        <Shield size={13} /> Grants locaux
                        <span className="text-stone-300 font-normal normal-case tracking-normal">({localGrants.length})</span>
                    </h3>
                    {!readOnly && (
                        <button
                            type="button"
                            onClick={() => { setShowAddForm(true); loadSubjects(); loadWikiGroups(); }}
                            className="btn btn-ghost btn-sm"
                        >
                            <Plus size={13} /> Ajouter un accès
                        </button>
                    )}
                </div>

                {localGrants.length > 0 && (
                    <div className="border border-stone-200 rounded-xl overflow-hidden mb-3">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-stone-50 text-stone-500 text-xs">
                                    <th className="text-left px-3 py-2 font-medium">Sujet</th>
                                    {PERM_KEYS.map((k) => (
                                        <th key={k} className="text-center px-1 py-2 font-medium w-16">{PERM_LABELS[k]}</th>
                                    ))}
                                    {!readOnly && <th className="w-10" />}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {localGrants.map((g, i) => (
                                    <tr key={i}>
                                        <td className="px-3 py-2"><GrantSubjectLabel grant={g} /></td>
                                        {PERM_KEYS.map((k) => (
                                            <td key={k} className="text-center px-1 py-2">
                                                {readOnly ? (
                                                    <PermBadge granted={g[k]} />
                                                ) : (
                                                    <input
                                                        type="checkbox"
                                                        checked={!!g[k]}
                                                        onChange={() => handleToggleLocalPerm(i, k)}
                                                        className="h-3.5 w-3.5 rounded border-stone-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                    />
                                                )}
                                            </td>
                                        ))}
                                        {!readOnly && (
                                            <td className="text-center px-1 py-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveLocal(i)}
                                                    className="p-1 rounded hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {showAddForm && (
                    <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/30 space-y-3">
                        {subjectsLoading ? (
                            <div className="flex items-center gap-2 text-sm text-stone-400 py-2">
                                <Loader2 size={14} className="animate-spin" /> Chargement des sujets…
                            </div>
                        ) : (
                            <>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <div className="flex-1">
                                        <label className="text-xs font-medium text-stone-600 mb-1 block">Type</label>
                                        <select
                                            value={addType}
                                            onChange={(e) => { setAddType(e.target.value); setAddTargetId(''); setAddRoleId(''); setAddWikiGroupId(''); setWikiGroupQuery(''); }}
                                            className="select"
                                        >
                                            <option value="user">Utilisateur</option>
                                            {hasProject && <option value="project_role">Rôle du projet</option>}
                                            <option value="wiki_role">Groupe Wiki</option>
                                        </select>
                                    </div>
                                    {addType === 'user' && (
                                        <div className="flex-1">
                                            <label className="text-xs font-medium text-stone-600 mb-1 block">Utilisateur</label>
                                            <select
                                                value={addTargetId}
                                                onChange={(e) => setAddTargetId(e.target.value)}
                                                className="select"
                                            >
                                                <option value="">— Sélectionner —</option>
                                                {(subjects?.users || []).map((u) => (
                                                    <option key={u.id} value={u.id}>{u.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    {addType === 'project_role' && (
                                        <div className="flex-1">
                                            <label className="text-xs font-medium text-stone-600 mb-1 block">Rôle</label>
                                            <select
                                                value={addRoleId}
                                                onChange={(e) => {
                                                    setAddRoleId(e.target.value);
                                                    if (subjects?.projectId) setAddTargetId(String(subjects.projectId));
                                                }}
                                                className="select"
                                            >
                                                <option value="">— Sélectionner —</option>
                                                {(subjects?.availableRoles || []).map((r) => (
                                                    <option key={r.id} value={r.id}>{r.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    {addType === 'wiki_role' && (
                                        <div className="flex-1" ref={wikiGroupRef}>
                                            <label className="text-xs font-medium text-stone-600 mb-1 block">Groupe wiki</label>
                                            {wikiGroupsLoading ? (
                                                <div className="flex items-center gap-2 text-sm text-stone-400 py-2">
                                                    <Loader2 size={14} className="animate-spin" /> Chargement…
                                                </div>
                                            ) : (
                                                <div className="relative">
                                                    <div className="relative">
                                                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                                                        <input
                                                            type="text"
                                                            value={wikiGroupQuery}
                                                            onChange={(e) => {
                                                                setWikiGroupQuery(e.target.value);
                                                                setShowWikiGroupDropdown(true);
                                                                if (!e.target.value.trim()) {
                                                                    setAddWikiGroupId('');
                                                                    setAddTargetId('');
                                                                }
                                                            }}
                                                            onFocus={() => setShowWikiGroupDropdown(true)}
                                                            placeholder="Rechercher un groupe…"
                                                            className="w-full pl-9 pr-8 py-2 border border-stone-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                                        />
                                                        {addWikiGroupId && (
                                                            <button
                                                                type="button"
                                                                onClick={() => { setAddWikiGroupId(''); setAddTargetId(''); setWikiGroupQuery(''); }}
                                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-600"
                                                            >
                                                                <X size={13} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {showWikiGroupDropdown && filteredWikiGroups.length > 0 && (
                                                        <ul className="absolute z-20 mt-1 w-full max-h-40 overflow-y-auto bg-white border border-stone-200 rounded-lg shadow-lg">
                                                            {filteredWikiGroups.map((wg) => (
                                                                <li key={wg.id}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setAddWikiGroupId(String(wg.id));
                                                                            setAddTargetId(String(wg.id));
                                                                            setWikiGroupQuery(wg.name);
                                                                            setShowWikiGroupDropdown(false);
                                                                        }}
                                                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${
                                                                            String(wg.id) === addWikiGroupId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-stone-700'
                                                                        }`}
                                                                    >
                                                                        {wg.name}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    {showWikiGroupDropdown && filteredWikiGroups.length === 0 && wikiGroupQuery.trim() && (
                                                        <div className="absolute z-20 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg px-3 py-2 text-sm text-stone-400 italic">
                                                            Aucun groupe trouvé
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-stone-600 mb-1.5 block">Permissions</label>
                                    <div className="flex flex-wrap gap-3">
                                        {PERM_KEYS.map((k) => (
                                            <label key={k} className="inline-flex items-center gap-1.5 text-sm text-stone-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={addPerms[k]}
                                                    onChange={() => setAddPerms((p) => ({ ...p, [k]: !p[k] }))}
                                                    className="h-3.5 w-3.5 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                {PERM_LABELS[k]}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-1">
                                    <button type="button" onClick={() => setShowAddForm(false)} className="btn btn-secondary btn-sm">
                                        Annuler
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleAddGrant}
                                        disabled={addType === 'user' ? !addTargetId : (addType === 'wiki_role' ? !addWikiGroupId : !addRoleId)}
                                        className="btn btn-primary btn-sm"
                                    >
                                        Ajouter
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </section>

            {children.length > 0 && (
                <section>
                    <button
                        type="button"
                        onClick={() => setChildrenOpen((v) => !v)}
                        className="flex items-center gap-2 text-xs font-semibold text-stone-400 uppercase tracking-widest hover:text-stone-600 transition-colors mb-3"
                    >
                        {childrenOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        <Folder size={13} />
                        Sous-répertoires ({children.length})
                    </button>
                    {childrenOpen && (
                        <div className="space-y-2">
                            {children.map((child) => (
                                <div key={child.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-stone-200 bg-white">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <Folder size={14} className="text-stone-400 shrink-0" aria-hidden="true" />
                                        <span className="text-sm font-medium text-stone-700 truncate">{child.name}</span>
                                    </div>
                                    <ChildStatusBadge child={child} />
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {error && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
            )}

            {!readOnly && (
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="btn btn-primary flex items-center gap-2"
                    >
                        {saving && <Loader2 size={14} className="animate-spin" />}
                        Enregistrer les accès
                    </button>
                </div>
            )}
        </div>
    );
}
